import type { ReactElement } from 'react';
import { useCallback } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

/**
 * Status scopes for the Network Discovery table. `all` clears the scope;
 * the others map 1:1 to the backend `status` query param. (`flagged` rows are
 * surfaced under the `approved` / "Identified" scope, matching the status
 * badge, so it is not a separate button.)
 */
export type NetworkStatusFilterValue =
  | 'pending'
  | 'approved'
  | 'ignored'
  | 'all';

const STATUS_TABS: ReadonlyArray<{
  value: NetworkStatusFilterValue;
  label: string;
}> = [
  { value: 'pending', label: 'Unreviewed' },
  { value: 'approved', label: 'Identified' },
  { value: 'ignored', label: 'Dismissed' },
  { value: 'all', label: 'All' },
];

/**
 * KIR-3496: top-level status segmented control for Network Discovery, mirroring
 * the Alerts screen's visibility selector. Fully controlled — the parent owns
 * the value and feeds it into the DataTable query.
 */
export function NetworkStatusFilterSelector({
  value,
  onChange,
  className,
}: {
  value: NetworkStatusFilterValue;
  onChange: (next: NetworkStatusFilterValue) => void;
  className?: string;
}): ReactElement {
  const handleValueChange = useCallback(
    (next: string): void => onChange(next as NetworkStatusFilterValue),
    [onChange]
  );

  return (
    <Tabs
      value={value}
      onValueChange={handleValueChange}
      className={className}
      data-slot="network-status-filter-selector"
    >
      <TabsList aria-label="Network Discovery status">
        {STATUS_TABS.map(tab => (
          <TabsTrigger key={tab.value} value={tab.value}>
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

NetworkStatusFilterSelector.displayName = 'NetworkStatusFilterSelector';
